import { useCallback, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { z } from "zod";
import { SlideOut } from "../../../../../components/SlideOut/SlideOut";
import {
  PrimaryButtonLargeV2,
  TertiaryButtonLargeV2,
} from "../../../../../components/Buttons/Buttons";
import type {
  AttributeSchema,
  CollectionSchema,
} from "../../../../../types/types.PIM";
import type { MethodsOfUseForm } from "../../../../../types/types";
import type {
  IRow,
  FormValue,
  MultiSelectRefType,
} from "../../PIM/components/ProductCollectionSchemaTable/type";
import {
  createFormFieldForCollection,
  getValidationForProductCollection,
} from "../../PIM/components/ProductCollectionSchemaTable/ProductCollectionSchema.util";
import type { DeepMap, FieldError } from "react-hook-form";
import { transformAddress } from "../../PIM/SellerAdminPIMAttributes/AddressAttributes/address.utils";
import {
  addressSchema,
  FieldWithIcons,
} from "../../PIM/components/ProductCollectionSchemaTable/CollectionSchemaSlideOut";
import { getEditBlockedReasonMessage } from "../../../../../util/attributeEditability";

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  padding-top: ${({ theme }) => theme.resolve("spacing_xl")};
  border-top: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.secondaryBorder};
  margin-top: ${({ theme }) => theme.resolve("spacing_xl")};
  padding-bottom: ${({ theme }) => theme.resolve("spacing_xl")};
  button {
    flex: 1;
  }
`;

interface EntityCollectionSlideOutProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (values: FormValue & { row_id: string }) => Promise<void>;
  columns: AttributeSchema[];
  row: IRow | null;
  rowUuid: string | null;
  isNewRow: boolean;
  isSubmitting: boolean;
  collection: CollectionSchema;
  excludeProfileId?: string;
  tenantId: string;
}

export function EntityCollectionSlideOut({
  show,
  onClose,
  onSubmit,
  columns,
  row,
  rowUuid,
  isNewRow,
  isSubmitting,
  collection,
  excludeProfileId,
  tenantId,
}: EntityCollectionSlideOutProps): JSX.Element | null {
  const { t } = useTranslation();
  const multiSelectRefs = useRef<{ [key: string]: MultiSelectRefType }>({});

  const editableColumns = useMemo(
    () => columns.filter((col) => col.input_type !== "multiline_entry"),
    [columns]
  );

  // Columns the user can actually change — excludes non-editable ones from
  // validation and submission so required-field checks don't block the form
  // and role-blocked values are never sent to the backend.
  const submittableColumns = useMemo(
    () => editableColumns.filter((col) => col.is_editable !== false),
    [editableColumns]
  );

  const validationSchema = useMemo(
    () =>
      z.object(
        submittableColumns.reduce<Record<string, z.ZodTypeAny>>((acc, col) => {
          if (col.input_type === "address") {
            acc[col.id] = col.is_not_applicable
              ? z.any()
              : z.preprocess((val) => {
                  if (!val || typeof val !== "object") return undefined;
                  const isEmpty = Object.values(val as object).every(
                    (v) => v === null || v === undefined || v === ""
                  );
                  return isEmpty ? undefined : val;
                }, addressSchema(t).optional());
          } else {
            acc[col.id] = getValidationForProductCollection({
              input_type: col.input_type,
              is_multiple_choice: col.is_multiple_choice ?? false,
              optional: !col.is_required,
              t,
            });
          }
          return acc;
        }, {})
      ),
    [submittableColumns, t]
  );

  const methods = useForm<FormValue>({
    resolver: zodResolver(validationSchema),
  });

  const { handleSubmit, errors, reset } = methods;

  // Reset form state when the slide-out closes or the row changes
  useEffect(() => {
    if (!show) {
      reset();
      multiSelectRefs.current = {};
    }
  }, [show, rowUuid, reset]);

  const handleFormSubmit = handleSubmit(async (values: FormValue) => {
    const processed: FormValue = {};
    Object.entries(values).forEach(([key, value]) => {
      const col = submittableColumns.find((c) => c.id === key);
      if (Array.isArray(value)) {
        processed[key] =
          value.length === 0
            ? ""
            : (value as Array<unknown>).map((val: unknown) => {
                if (val && typeof val === "object") {
                  if ("name" in (val as Record<string, unknown>)) {
                    return (val as { name: string }).name;
                  }
                  if ("value" in (val as Record<string, unknown>)) {
                    return (val as { value: string }).value;
                  }
                }
                return val as string;
              });
      } else if (col?.input_type === "address") {
        processed[key] =
          transformAddress(value as Record<string, any>) ?? undefined;
      } else {
        processed[key] = value instanceof Date ? value.toISOString() : value;
      }
    });
    await onSubmit({ ...processed, row_id: rowUuid ?? "" });
  });

  const formRow: IRow = row ?? {
    uuid: rowUuid ?? "",
    edit: true,
    row_editable: true,
  };

  const title = isNewRow
    ? t("Add New {{name}}", {
        name: collection.display_name ?? collection.name,
      })
    : t("Edit {{name}}", { name: collection.display_name ?? collection.name });

  const colWithDefaultValues = useCallback(
    (col: AttributeSchema) => {
      return {
        ...col,
        default_values:
          collection.value_rows
            ?.find((row_item) => row_item.row_id === rowUuid)
            ?.values?.filter((value) => value.attribute_id === col.id) ?? null,
      };
    },
    [collection.value_rows, rowUuid]
  );

  return (
    <SlideOut show={show} closeFlyout={onClose} title={title}>
      <FormWrapper>
        <Form
          noValidate
          onSubmit={handleFormSubmit}
          id="entity-collection-row-form"
        >
          {editableColumns.map((col) => {
            const value = row ? row[col.id] : null;
            return (
              <FieldWrapper key={col.id}>
                <FieldWithIcons
                  fieldId={col.id}
                  description={col.description ? t([col.description]) : null}
                  restricted={col.is_restricted}
                  notApplicable={col.is_not_applicable}
                  disabledTooltip={
                    getEditBlockedReasonMessage(col.edit_blocked_reason, t) ||
                    undefined
                  }
                >
                  {createFormFieldForCollection({
                    col: colWithDefaultValues(col),
                    value,
                    row: formRow,
                    methodsOfUseForm: methods as unknown as MethodsOfUseForm,
                    errors: (errors ?? {}) as DeepMap<FormValue, FieldError>,
                    tenant_id: tenantId,
                    t,
                    has_reference_entity_mapping: false,
                    multiSelectRefs,
                    excludeProfileId,
                  })}
                </FieldWithIcons>
              </FieldWrapper>
            );
          })}
        </Form>
        <ButtonContainer>
          <TertiaryButtonLargeV2 type="button" onClick={onClose}>
            {t("Cancel")}
          </TertiaryButtonLargeV2>
          <PrimaryButtonLargeV2
            type="submit"
            form="entity-collection-row-form"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {t("Save")}
          </PrimaryButtonLargeV2>
        </ButtonContainer>
      </FormWrapper>
    </SlideOut>
  );
}
